package xim.poc.game.configuration.v0.tactics

import xim.math.Vector2f
import xim.poc.Font
import xim.poc.TextAlignment
import xim.poc.UiElementHelper
import xim.poc.audio.AudioManager
import xim.poc.audio.SystemSound
import xim.poc.game.ActorType
import xim.poc.game.GameEngine.displayName
import xim.poc.game.ScrollSettings
import xim.poc.game.UiState
import xim.poc.game.UiStateHelper
import xim.poc.game.configuration.constants.MobSkillId
import xim.poc.game.configuration.constants.SkillId
import xim.poc.game.configuration.constants.mskillNull_0
import xim.poc.game.configuration.v0.TacticsUnit
import xim.poc.gl.Color
import xim.poc.tools.UiPosition
import xim.poc.ui.ChatLog
import xim.poc.ui.ChatLogColor
import xim.poc.ui.ShiftJis

private var describedSelf = false

class TacticsInheritanceUi(val unit: TacticsUnit) {

    private val inheritanceOptions = computeInheritance()

    private val help: UiState
    private val inventory: UiState
    private val equip: UiState
    private val capacity: UiState

    init {
        help = UiState(
            focusMenu = "menu    bluehelp",
            additionalDraw = { drawHelp() },
            uiPositionKey = UiPosition.Equipment,
        ) {
            false
        }

        capacity = UiState(
            focusMenu = "menu    bluepoin",
            additionalDraw = { drawCapacity() },
            uiPositionKey = UiPosition.Equipment,
        ) {
            false
        }

        inventory = UiState(
            focusMenu = "menu    bluinven",
            additionalDraw = { drawBlueInventory() },
            scrollSettings = ScrollSettings(10) { inheritanceOptions.size },
            resetCursorIndexOnPush = false,
            childStates = { listOf(help, capacity) },
            drawParent = true,
            locksMovement = true,
            uiPositionKey = UiPosition.Equipment,
        ) {
            if (UiStateHelper.isEnterPressed()) {
                val success = equipSpell()
                if (success) { UiStateHelper.popState(SystemSound.MenuSelect) }
                true
            } else if (UiStateHelper.isEscPressed()) {
                UiStateHelper.popState(SystemSound.MenuClose)
                true
            } else {
                false
            }
        }

        equip = UiState(
            focusMenu = "menu    bluequip",
            additionalDraw = { drawBlueEquip(); TacticsUnitDescription.drawUnitDescription(unit, ActorType.AllyNpc) },
            childStates = { listOf(inventory, help, capacity) },
            scrollSettings = ScrollSettings(10) { unit.inheritance.size },
            locksMovement = true,
            uiPositionKey = UiPosition.Equipment,
        ) {
            if (UiStateHelper.isEnterPressed()) {
                UiStateHelper.pushState(inventory, SystemSound.MenuSelect)
                true
            } else if (UiStateHelper.isEscPressed()) {
                UiStateHelper.popState(SystemSound.MenuClose)
                true
            } else {
                false
            }
        }

    }

    fun push() {
        if (!describedSelf) { describeSelf() }
        UiStateHelper.pushState(equip)
    }

    private fun getEquippedSpells(): List<MobSkillId> {
        return unit.inheritance.toList()
    }

    private fun equipSpell(): Boolean {
        val equipSlot = getSelectedIndex(equip)
        if (equipSlot >= unit.inheritance.size) { return false }

        val inventorySlot = getSelectedIndex(inventory)
        val inventorySpell = inheritanceOptions.getOrNull(inventorySlot) ?: return false

        val currentlyEquippedSlot = unit.inheritance.indexOfFirst { it == inventorySpell }

        // Remove
        if (currentlyEquippedSlot == equipSlot) {
            unit.inheritance[equipSlot] = mskillNull_0
            return true
        }

        // Swap with already equipped
        if (currentlyEquippedSlot >= 0) {
            unit.inheritance[currentlyEquippedSlot] = unit.inheritance[equipSlot]
            unit.inheritance[equipSlot] = inventorySpell
            return true
        }

        // Try to equip new
        val existingSpell = unit.inheritance[equipSlot]
        if (!canAfford(inventorySpell = inventorySpell, existingSpell = existingSpell)) {
            ChatLog("Equipping ${ShiftJis.colorItem}${inventorySpell.displayName()}${ShiftJis.colorClear} would exceed maximum points.", ChatLogColor.Error)
            AudioManager.playSystemSoundEffect(SystemSound.Invalid)
            return false
        }

        unit.inheritance[equipSlot] = inventorySpell
        return true
    }

    private fun drawBlueEquip() {
        val scrollSettings = equip.scrollSettings ?: return
        val stackPos = equip.latestPosition ?: return

        val offset = Vector2f()

        for (i in 0 until 10) {
            val itemIndex = scrollSettings.lowestViewableItemIndex + i

            val current = i == equip.cursorIndex
            val mask = if (current) { Color(1f, 0.75f, 0f, 1f) } else { Color.NO_MASK }
            UiElementHelper.drawUiElement(lookup = "menu    keytops3", index = 400+itemIndex, color = mask, position = offset + stackPos + Vector2f(4f, 4f))

            val skill = getEquippedSpells().getOrNull(itemIndex)

            if (skill != null && skill != mskillNull_0) {
                UiElementHelper.drawString(text = skill.displayName(), offset = offset + stackPos + Vector2f(42f, 8f))
                val (spellIcon, spellIndex) = UiElementHelper.getSkillIcon(skill) ?: continue
                UiElementHelper.drawUiElement(lookup = spellIcon, index = spellIndex, position = offset + stackPos + Vector2f(22f, 4f))
            }

            offset.y += 18f
        }
    }

    private fun drawBlueInventory() {
        val scrollSettings = inventory.scrollSettings ?: return
        val stackPos = inventory.latestPosition ?: return

        val offset = Vector2f()

        val equipSlot = getSelectedIndex(equip)
        val equipSpell = unit.inheritance[equipSlot]

        for (i in 0 until 10) {
            val itemIndex = scrollSettings.lowestViewableItemIndex + i
            val skill = inheritanceOptions.getOrNull(itemIndex)

            if (skill != null) {
                val textColor = if (isEquipped(skill)) {
                    ShiftJis.colorItem
                } else if (!canAfford(inventorySpell = skill, existingSpell = equipSpell)) {
                    ShiftJis.colorGrey
                } else {
                    ShiftJis.colorWhite
                }

                val spellName = "$textColor${skill.displayName()}${ShiftJis.colorClear}"

                UiElementHelper.drawString(text = spellName, offset = offset + stackPos + Vector2f(22f, 8f))

                val (spellIcon, spellIndex) = UiElementHelper.getSkillIcon(skill) ?: continue
                UiElementHelper.drawUiElement(lookup = spellIcon, index = spellIndex, position = offset + stackPos + Vector2f(4f, 4f))
            }

            offset.y += 18f
        }
    }

    private fun canAfford(inventorySpell: SkillId, existingSpell: SkillId): Boolean {
        val newSpellCost = TacticsSkills[inventorySpell].crossJob?.points ?: throw IllegalStateException("${inventorySpell.displayName()} is not x-job")
        val existingSpellCost = if (existingSpell == mskillNull_0) { 0 } else { TacticsSkills[existingSpell].crossJob?.points ?: 0 }

        val pointDelta = newSpellCost - existingSpellCost
        val (currentPoints, maximumPoints) = getPointTotalAndMaximum()

        return currentPoints + pointDelta <= maximumPoints
    }

    private fun drawHelp() {
        val skill = if (UiStateHelper.isFocus(equip)) {
            getEquippedSpells().getOrNull(getSelectedIndex(equip)) ?: return
        } else {
            inheritanceOptions.getOrNull(getSelectedIndex(inventory)) ?: return
        }

        if (skill == mskillNull_0) { return }
        TacticsSkillSelectUi.drawSkillDescription(state = help, skill = skill, actorState = null, unit = unit, inheritanceContext = true)
    }

    private fun drawCapacity() {
        val position = capacity.latestPosition ?: return

        val (current, maximum) = getPointTotalAndMaximum()
        val color = if (current >= maximum) { ShiftJis.colorKey } else { ShiftJis.colorWhite }

        UiElementHelper.drawString("$color$current/$maximum", offset = position + Vector2f(50f, 24f), alignment = TextAlignment.Center, font = Font.FontShp)
    }

    private fun getSelectedIndex(uiState: UiState): Int {
        return uiState.getCurrentItemIndex()
    }

    private fun isEquipped(skillId: SkillId): Boolean {
        return getEquippedSpells().any { it == skillId }
    }

    private fun computeInheritance(): List<MobSkillId> {
        val inheritance = HashSet<MobSkillId>()

        for (playerUnit in TacticsState.getPlayerUnits()) {
            inheritance += computeInheritance(playerUnit)
        }

        inheritance.removeAll(unit.computeSelfSkills())
        return inheritance.toList().sortedBy { it.displayName() }
    }

    private fun computeInheritance(unit: TacticsUnit): Set<MobSkillId> {
        val inheritance = HashSet<MobSkillId>()

        for ((jobId, jobLevel) in unit.jobLevels) {
            val jobConfig = TacticsJobConfigs[jobId]

            inheritance += jobConfig.skillsByLevel.filter { jobLevel.level >= it.key }
                .values.flatten()
                .filterIsInstance<MobSkillId>()
                .filter { TacticsSkills[it].isCrossJob() }
        }

        return inheritance
    }

    private fun getPointTotalAndMaximum(): Pair<Int, Int> {
        val current = unit.inheritance.filter { it != mskillNull_0 }
            .mapNotNull { TacticsSkills[it].crossJob?.points }
            .sum()

        val maximum = 3 + unit.highestLevel/5

        return current to maximum
    }

    private fun describeSelf() {
        describedSelf = true

        ChatLog("Equip ${ShiftJis.colorAug}\"All Job\" Monster Skills${ShiftJis.colorClear} that your other units have learned!", ChatLogColor.Info)
        ChatLog("${ShiftJis.solidSquare}Units gain 1 maximum capacity per ${ShiftJis.colorAug}5 Unit Levels${ShiftJis.colorClear}.", ChatLogColor.Info)
        ChatLog("${ShiftJis.solidSquare}If applicable, inherited skills have ${ShiftJis.colorInvalid}half charges${ShiftJis.colorAug}.", ChatLogColor.Info)
        ChatLog("${ShiftJis.solidSquare}Units will automatically have access to every ${ShiftJis.colorAug}\"All Job\" skill${ShiftJis.colorClear} that the unit itself has learned.", ChatLogColor.Info)
    }

}
